import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import {
  Box,
  HeadingText,
  ParagraphText,
  StackItem,
  StackLayout,
  queryHelpers,
  useMediaQuery,
} from "@leagueplatform/genesis-core";
import { PageHeaderBackground } from "@leagueplatform/web-common-components";
import { BackButtonArrow } from "components/back-button-arrow/back-button-arrow.component";

export type PageHeaderVariant = "greeting" | "section" | "sub-page";

export type PageHeaderMobileBand = "green" | "none";

export type PageHeaderProps = {
  /** When omitted, renders the legacy layout. */
  variant?: PageHeaderVariant;
  /** Mobile band treatment. Defaults to `green`. */
  mobileBand?: PageHeaderMobileBand;
  /** Heading text to render. */
  headingText?: string;
  /** Optional supporting copy. Used by `greeting` and `section`. */
  subtitle?: string;
  /** Slot rendered opposite the heading. Honoured only by `greeting`. */
  trailingContent?: React.ReactNode;
  /** Defaults to `true` for `sub-page`, `false` otherwise. */
  showBackButton?: boolean;
};

const HEADER_BG_PADDING_LAPTOP_DOWN = 80;
const HEADER_BG_PADDING_DEFAULT = 100;

const SUB_PAGE_DESKTOP_BAND_HEIGHT = "128px";
const SUB_PAGE_MOBILE_NO_BAND_HEIGHT = "48px";

const LegacyPageHeader = ({
  headingText,
  showBackButton = true,
}: Pick<PageHeaderProps, "headingText" | "showBackButton">) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(100);
  const isLaptopDown = useMediaQuery(queryHelpers.down("laptop"));
  const backgroundHeightPadding = isLaptopDown
    ? HEADER_BG_PADDING_LAPTOP_DOWN
    : HEADER_BG_PADDING_DEFAULT;

  useLayoutEffect(() => {
    const reportWindowSize = () => {
      setContentHeight(contentRef?.current?.clientHeight ?? contentHeight);
    };
    if (contentHeight !== undefined)
      window.addEventListener("resize", reportWindowSize);
    return () => window.removeEventListener("resize", reportWindowSize);
  }, [contentHeight]);

  useEffect(() => {
    if (contentRef?.current?.clientHeight)
      setContentHeight(contentRef.current.clientHeight);
  }, []);

  return (
    <Box
      css={{
        marginTop: "$two",
        marginBottom: "$two",
        "@laptop": { marginTop: "56px", marginBottom: "56px" },
        "@desktop": { marginTop: "56px", marginBottom: "56px" },
      }}
    >
      <PageHeaderBackground
        height={`${contentHeight + backgroundHeightPadding / 2}px`}
        pageHeaderBackgroundHeight={`${
          contentHeight + backgroundHeightPadding
        }px`}
      >
        <StackLayout>
          <StackItem
            horizontalAlignment="start"
            verticalAlignment="top"
            ref={contentRef}
          >
            <StackLayout
              orientation={{
                "@initial": "vertical",
                "@desktop": "horizontal",
                "@laptop": "horizontal",
              }}
            >
              {showBackButton && (
                <BackButtonArrow
                  css={
                    isLaptopDown
                      ? { marginBottom: "$half" }
                      : { marginTop: "$half", marginRight: "$one" }
                  }
                  analyticsEventProps={{ screen_name: headingText }}
                />
              )}

              {headingText ? (
                <HeadingText level="1" size="xl">
                  {headingText}
                </HeadingText>
              ) : (
                <Box css={{ height: "$three" }} />
              )}
            </StackLayout>
          </StackItem>
        </StackLayout>
      </PageHeaderBackground>
    </Box>
  );
};

const PageHeaderSurface = ({
  mobileBand,
  children,
}: {
  mobileBand: PageHeaderMobileBand;
  children: React.ReactNode;
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(100);
  const isLaptopDown = useMediaQuery(queryHelpers.down("laptop"));
  const isMobile = useMediaQuery(queryHelpers.down("tablet"));
  const backgroundHeightPadding = isLaptopDown
    ? HEADER_BG_PADDING_LAPTOP_DOWN
    : HEADER_BG_PADDING_DEFAULT;

  useLayoutEffect(() => {
    const reportWindowSize = () => {
      setContentHeight(contentRef?.current?.clientHeight ?? contentHeight);
    };
    window.addEventListener("resize", reportWindowSize);
    return () => window.removeEventListener("resize", reportWindowSize);
  }, [contentHeight]);

  useEffect(() => {
    if (contentRef?.current?.clientHeight)
      setContentHeight(contentRef.current.clientHeight);
  }, []);

  const showGreenBand = !isMobile || mobileBand === "green";

  const inner = (
    <div ref={contentRef}>
      <Box css={{ paddingBlock: showGreenBand ? "$one" : "$two" }}>
        {children}
      </Box>
    </div>
  );

  return (
    <Box
      css={{
        marginTop: "$two",
        marginBottom: "$two",
        "@laptop": { marginTop: "56px", marginBottom: "56px" },
        "@desktop": { marginTop: "56px", marginBottom: "56px" },
      }}
    >
      {showGreenBand ? (
        <PageHeaderBackground
          height={`${contentHeight + backgroundHeightPadding / 2}px`}
          pageHeaderBackgroundHeight={`${
            contentHeight + backgroundHeightPadding
          }px`}
        >
          {inner}
        </PageHeaderBackground>
      ) : (
        inner
      )}
    </Box>
  );
};

const GreetingVariant = ({
  heading,
  subtitle,
  trailingContent,
}: {
  heading: string;
  subtitle?: string;
  trailingContent?: React.ReactNode;
}) => (
  <StackLayout
    orientation="horizontal"
    horizontalAlignment="spaceBetween"
    verticalAlignment="center"
    css={{ gap: "$one" }}
  >
    <StackItem css={{ flex: "1 1 auto", minWidth: 0 }}>
      {heading ? (
        <HeadingText level="1" size="xl">
          {heading}
        </HeadingText>
      ) : null}
      {subtitle ? (
        <ParagraphText css={{ marginTop: "$quarter" }}>
          {subtitle}
        </ParagraphText>
      ) : null}
    </StackItem>
    {trailingContent ? (
      <StackItem css={{ flex: "0 0 auto" }}>{trailingContent}</StackItem>
    ) : null}
  </StackLayout>
);

const SectionVariant = ({
  heading,
  subtitle,
}: {
  heading: string;
  subtitle?: string;
}) => (
  <StackLayout orientation="vertical">
    {heading ? (
      <HeadingText level="1" size="xl">
        {heading}
      </HeadingText>
    ) : null}
    {subtitle ? (
      <ParagraphText css={{ marginTop: "$half" }}>{subtitle}</ParagraphText>
    ) : null}
  </StackLayout>
);

const SubPageVariant = ({
  heading,
  showBackButton,
  mobileBand,
}: {
  heading: string;
  showBackButton: boolean;
  mobileBand: PageHeaderMobileBand;
}) => {
  const isMobile = useMediaQuery(queryHelpers.down("tablet"));

  if (isMobile && mobileBand === "none") {
    return (
      <Box
        css={{
          position: "relative",
          height: SUB_PAGE_MOBILE_NO_BAND_HEIGHT,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {showBackButton && (
          <Box
            css={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            <BackButtonArrow analyticsEventProps={{ screen_name: heading }} />
          </Box>
        )}
        {heading ? (
          <HeadingText level="1" size="sm">
            {heading}
          </HeadingText>
        ) : null}
      </Box>
    );
  }

  return (
    <PageHeaderBackground
      height={SUB_PAGE_DESKTOP_BAND_HEIGHT}
      pageHeaderBackgroundHeight={SUB_PAGE_DESKTOP_BAND_HEIGHT}
    >
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        css={{ height: "100%", gap: "$half" }}
      >
        {showBackButton && (
          <BackButtonArrow
            css={{ marginRight: "$half" }}
            analyticsEventProps={{ screen_name: heading }}
          />
        )}
        {heading ? (
          <HeadingText level="1" size="xl">
            {heading}
          </HeadingText>
        ) : null}
      </StackLayout>
    </PageHeaderBackground>
  );
};

export const PageHeader = ({
  variant,
  mobileBand = "green",
  headingText,
  subtitle,
  trailingContent,
  showBackButton,
}: PageHeaderProps) => {
  if (!variant) {
    return (
      <LegacyPageHeader
        headingText={headingText}
        showBackButton={showBackButton}
      />
    );
  }

  const resolvedHeading = headingText ?? "";
  const resolvedShowBackButton = showBackButton ?? variant === "sub-page";

  if (variant === "sub-page") {
    return (
      <Box
        css={{
          marginBottom: "$two",
          "@laptop": { marginBottom: "56px" },
          "@desktop": { marginBottom: "56px" },
        }}
      >
        <SubPageVariant
          heading={resolvedHeading}
          showBackButton={resolvedShowBackButton}
          mobileBand={mobileBand}
        />
      </Box>
    );
  }

  return (
    <PageHeaderSurface mobileBand={mobileBand}>
      {variant === "greeting" && (
        <GreetingVariant
          heading={resolvedHeading}
          subtitle={subtitle}
          trailingContent={trailingContent}
        />
      )}
      {variant === "section" && (
        <SectionVariant heading={resolvedHeading} subtitle={subtitle} />
      )}
    </PageHeaderSurface>
  );
};
